import { useState, useEffect, useCallback } from 'react';
import { flujoCajaApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { NumberInput } from '../NumberInput';
import { Label } from '../ui/label';
import { Skeleton } from '../ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../../hooks/use-toast';
import { Check, Download, Pencil, Plus, Trash2, TrendingUp, AlertTriangle, CalendarClock } from 'lucide-react';

const formatCLP = (value) => {
  if (!value || value === 0) return '';
  return new Intl.NumberFormat('es-CL', { maximumFractionDigits: 0 }).format(value);
};

const formatCLPFull = (value) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(value || 0);

const MESES_NOMBRES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

// Celda "Vencido": muestra el total en rojo; al hacer clic abre el detalle
// (qué meses/montos lo componen) con acciones de reprogramar a mes futuro o
// marcar pagado. Debajo, input para cargar/ajustar el vencido manual.
const VencidoCell = ({ item, mesesHeader, onManualBlur, onMarcarPagado, onReprogramar }) => {
  const [target, setTarget] = useState('');
  const [manual, setManual] = useState(item.vencido_manual || 0);
  useEffect(() => { setManual(item.vencido_manual || 0); }, [item.vencido_manual]);
  const futuros = (mesesHeader || []).filter((_, i) => i >= 1); // 11 meses futuros de la ventana
  return (
    <td
      className="sticky left-[536px] z-10 bg-white dark:bg-slate-950 border px-1 py-1 text-right align-top"
      data-testid={`proy-vencido-${item.orden}`}
    >
      {item.vencido > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className="flex items-center justify-end gap-1 w-full text-red-600 dark:text-red-400 font-semibold mb-0.5 hover:underline"
              title="Ver detalle del vencido (reprogramar / marcar pagado)"
              data-testid={`btn-vencido-detalle-${item.orden}`}
            >
              <AlertTriangle className="w-3 h-3 flex-shrink-0" />
              <span className="tabular-nums">{formatCLP(item.vencido)}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 text-left" data-testid={`popover-vencido-${item.orden}`}>
            <div className="space-y-3">
              <p className="text-sm font-semibold flex items-center gap-1.5 text-red-600 dark:text-red-400">
                <AlertTriangle className="w-4 h-4" /> Vencido — {item.nombre}
              </p>
              <div className="border rounded divide-y text-xs">
                {(item.vencido_detalle || []).map((v) => (
                  <div key={v.ym} className="flex justify-between px-2 py-1">
                    <span className={v.tipo === 'manual' ? 'italic text-muted-foreground' : ''}>{v.label}</span>
                    <span className="tabular-nums font-medium">{formatCLPFull(v.monto)}</span>
                  </div>
                ))}
                <div className="flex justify-between px-2 py-1 bg-red-50 dark:bg-red-950/40 font-semibold">
                  <span>Total vencido</span>
                  <span className="tabular-nums text-red-600 dark:text-red-400">{formatCLPFull(item.vencido)}</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                  <CalendarClock className="w-3.5 h-3.5" /> Reprogramar a un mes futuro:
                </p>
                <div className="flex gap-1.5">
                  <Select value={target} onValueChange={setTarget}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`select-reprog-${item.orden}`}>
                      <SelectValue placeholder="Elegir mes…" />
                    </SelectTrigger>
                    <SelectContent>
                      {futuros.map((m) => (
                        <SelectItem key={m.ym} value={m.ym} className="text-xs">
                          {MESES_NOMBRES[m.month - 1]} {m.year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm" className="h-8 text-xs" disabled={!target}
                    onClick={() => onReprogramar(item, target)}
                    data-testid={`btn-reprogramar-${item.orden}`}
                  >
                    Reprogramar
                  </Button>
                </div>
              </div>
              <Button
                size="sm" variant="outline"
                className="w-full h-8 text-xs text-emerald-700 dark:text-emerald-400 border-emerald-300"
                onClick={() => onMarcarPagado(item)}
                data-testid={`btn-vencido-pagado-${item.orden}`}
              >
                <Check className="w-3 h-3 mr-1" /> Marcar como pagado (limpiar)
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      )}
      <NumberInput
        allowDecimal={false}
        allowNegative={false}
        value={manual}
        onChange={(clean) => setManual(clean === '' ? 0 : Number(clean))}
        onBlur={() => onManualBlur(item, manual)}
        placeholder="+ manual"
        title="Cargar/ajustar vencido a mano (se suma al arrastrado)"
        className="h-6 w-full text-right text-[11px] tabular-nums border-dashed rounded-none focus-visible:ring-1 focus-visible:ring-red-400"
        data-testid={`input-vencido-manual-${item.orden}`}
      />
    </td>
  );
};

const emptyForm = () => ({
  orden: 0,
  codigo: 0,
  nro: '',
  nombre: '',
  total: 0,
  meses: Array(12).fill(0),
  pagados: Array(12).fill(false),
  dia_tipico: 20,
  vencido_manual: 0,
});

export default function Proyeccion12mPanel() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ meses_header: [], items: [], total_por_mes: [] });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await flujoCajaApi.listProyeccion();
      setData(res.data);
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error al cargar proyección', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    const maxOrden = data.items.length > 0 ? Math.max(...data.items.map(i => i.orden)) : 0;
    setForm({ ...emptyForm(), orden: maxOrden + 1 });
    setEditing('new');
  };

  const openEdit = (item) => {
    setForm({
      orden: item.orden,
      codigo: item.codigo,
      nro: item.nro,
      nombre: item.nombre,
      total: item.total,
      meses: [...item.meses],
      pagados: [...(item.pagados || Array(12).fill(false))],
      dia_tipico: item.dia_tipico || 20,
      vencido_manual: item.vencido_manual || 0,
    });
    setEditing(item);
  };

  const close = () => { setEditing(null); setForm(emptyForm()); };

  const save = async () => {
    if (!form.nombre.trim()) { toast({ title: 'Nombre requerido', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      if (editing === 'new') {
        await flujoCajaApi.createProyeccion(form);
        toast({ title: 'Fila agregada' });
      } else {
        await flujoCajaApi.updateProyeccion(editing.id, form);
        toast({ title: 'Fila actualizada' });
      }
      close();
      load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error al guardar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (item) => {
    if (!window.confirm(`¿Eliminar "${item.nombre}"?`)) return;
    try {
      await flujoCajaApi.deleteProyeccion(item.id);
      toast({ title: 'Fila eliminada' });
      load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };

  const updateMes = (idx, val) => {
    const meses = [...form.meses];
    meses[idx] = Number(val) || 0;
    setForm({ ...form, meses });
  };

  const togglePagadoInline = async (item, mesIdx) => {
    try {
      // Optimistic update
      setData((prev) => ({
        ...prev,
        items: prev.items.map((it) => {
          if (it.id !== item.id) return it;
          const newPag = [...(it.pagados || Array(12).fill(false))];
          newPag[mesIdx] = !newPag[mesIdx];
          return { ...it, pagados: newPag };
        }),
      }));
      await flujoCajaApi.toggleMesPagado(item.id, mesIdx);
      // Recargar para recalcular totales
      load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error al marcar pagado', variant: 'destructive' });
      load();
    }
  };

  const saveDiaTipicoInline = async (item, valor) => {
    const dia = Math.max(1, Math.min(31, Number(valor) || 20));
    if (dia === (item.dia_tipico || 20)) return;
    try {
      await flujoCajaApi.updateProyeccion(item.id, { ...item, dia_tipico: dia });
      toast({ title: `Día típico actualizado a ${dia}` });
      load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };

  const saveVencidoManualInline = async (item, valor) => {
    const monto = Math.max(0, Number(valor) || 0);
    if (monto === (item.vencido_manual || 0)) return;
    try {
      await flujoCajaApi.setVencidoManual(item.id, monto);
      toast({ title: monto > 0 ? `Vencido manual: $${monto.toLocaleString('es-CL')}` : 'Vencido manual en cero' });
      load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };

  const marcarVencidoPagado = async (item) => {
    if (!window.confirm(`¿Marcar como pagado el vencido de "${item.nombre}"? Se limpiará de la proyección y del flujo de caja.`)) return;
    try {
      await flujoCajaApi.marcarVencidoPagado(item.id);
      toast({ title: 'Vencido marcado como pagado' });
      load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };

  const reprogramarVencido = async (item, targetYm) => {
    if (!targetYm) return;
    const [ty, tm] = targetYm.split('-');
    const label = `${MESES_NOMBRES[parseInt(tm, 10) - 1]} ${ty}`;
    try {
      const { data: res } = await flujoCajaApi.reprogramarVencido(item.id, targetYm);
      toast({ title: `Vencido reprogramado a ${label}`, description: `$${(res?.monto_reprogramado || 0).toLocaleString('es-CL')} movido al mes destino` });
      load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error al reprogramar', variant: 'destructive' });
    }
  };

  return (
    <Card data-testid="proyeccion-12m-panel">
      <CardHeader className="pb-3">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="w-4 h-4 text-emerald-600" /> Proyección a 12 Meses
            </CardTitle>
            <CardDescription>
              Gastos proyectados por categoría y mes calendario. <strong>Día</strong>: día típico de pago en el mes (ej: IVA=20, Sueldos=5). <strong>Clic en celda</strong> para marcarla pagada. La columna <strong className="text-red-600">Vencido</strong> arrastra lo no pagado de meses anteriores (editable a mano); márcalo pagado para limpiarlo.
            </CardDescription>
          </div>
          <div className="flex items-center gap-1.5">
            <Button size="sm" onClick={openNew} data-testid="btn-new-proyeccion">
              <Plus className="w-4 h-4 mr-1" /> Nueva fila
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {loading ? (
          <div className="p-4"><Skeleton className="h-40" /></div>
        ) : (
          <div className="overflow-x-auto max-h-[60vh]" data-testid="proyeccion-scroll">
            <table className="text-xs border-collapse w-full">
              <thead className="sticky top-0 z-20 bg-slate-100 dark:bg-slate-900 shadow-sm">
                <tr>
                  <th className="sticky left-0 z-30 bg-slate-100 dark:bg-slate-900 border px-2 py-1.5 w-[64px] text-center">Acciones</th>
                  <th className="sticky left-[64px] z-30 bg-slate-100 dark:bg-slate-900 border px-2 py-1.5 w-10 text-center">#</th>
                  <th className="sticky left-[104px] z-30 bg-slate-100 dark:bg-slate-900 border px-2 py-1.5 text-left w-60 min-w-60">Nombre</th>
                  <th className="sticky left-[344px] z-30 bg-slate-100 dark:bg-slate-900 border px-2 py-1.5 text-center w-16 min-w-16" title="Día típico de pago en el mes (1-31). Se usa para inyectar el valor al grid día a día.">Día</th>
                  <th className="sticky left-[408px] z-30 bg-slate-100 dark:bg-slate-900 border px-2 py-1.5 text-right w-32 min-w-32">Total</th>
                  <th className="sticky left-[536px] z-30 bg-red-50 dark:bg-red-950/40 border px-2 py-1.5 text-right w-28 min-w-28 text-red-700 dark:text-red-300" title="Vencido: lo no pagado de meses anteriores (arrastre automático) + monto manual. Se suma a la columna de HOY del flujo de caja.">Vencido</th>
                  {data.meses_header.map((m) => (
                    <th key={m.key} className="border px-2 py-1.5 text-center min-w-[110px]" data-testid={`proy-header-${m.key}`}>
                      <div className="font-semibold">{MESES_NOMBRES[m.month - 1]}</div>
                      <div className="text-[10px] text-muted-foreground">{m.year}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={item.id} className="hover:bg-muted/30 border-t" data-testid={`proy-row-${item.orden}`}>
                    <td className="sticky left-0 z-10 bg-white dark:bg-slate-950 border px-1 py-1 text-center whitespace-nowrap">
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => openEdit(item)} data-testid={`btn-edit-proy-${item.orden}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-red-600" onClick={() => doDelete(item)} data-testid={`btn-del-proy-${item.orden}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </td>
                    <td className="sticky left-[64px] z-10 bg-white dark:bg-slate-950 border px-2 py-1 tabular-nums text-center">{item.orden}</td>
                    <td className="sticky left-[104px] z-10 bg-white dark:bg-slate-950 border px-2 py-1 font-medium truncate max-w-60" title={item.nombre}>{item.nombre}</td>
                    <td className="sticky left-[344px] z-10 bg-white dark:bg-slate-950 border p-0 text-center">
                      <Input
                        type="number"
                        min={1}
                        max={31}
                        value={item.dia_tipico || 20}
                        onChange={(e) => {
                          const v = Math.max(1, Math.min(31, Number(e.target.value) || 20));
                          // Optimistic local update
                          setData((prev) => ({
                            ...prev,
                            items: prev.items.map((it) => it.id === item.id ? { ...it, dia_tipico: v } : it),
                          }));
                        }}
                        onBlur={(e) => saveDiaTipicoInline(item, e.target.value)}
                        className="h-7 w-14 text-center text-xs tabular-nums border-0 rounded-none focus-visible:ring-1 focus-visible:ring-amber-400"
                        data-testid={`input-dia-tipico-${item.orden}`}
                      />
                    </td>
                    <td className="sticky left-[408px] z-10 bg-white dark:bg-slate-950 border px-2 py-1 text-right tabular-nums font-semibold">{formatCLP(item.total)}</td>
                    <VencidoCell
                      item={item}
                      mesesHeader={data.meses_header}
                      onManualBlur={saveVencidoManualInline}
                      onMarcarPagado={marcarVencidoPagado}
                      onReprogramar={reprogramarVencido}
                    />
                    {item.meses.map((v, i) => {
                      const pagado = (item.pagados || [])[i] === true;
                      return (
                        <td
                          key={i}
                          className={`border px-1 py-1 text-right tabular-nums group relative transition-colors ${
                            pagado
                              ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 line-through opacity-70'
                              : v > 0
                                ? 'hover:bg-amber-50 dark:hover:bg-amber-950/20 cursor-pointer'
                                : ''
                          }`}
                          onClick={() => v > 0 && togglePagadoInline(item, i)}
                          title={
                            v > 0
                              ? pagado
                                ? `✓ Pagado — clic para desmarcar (${formatCLPFull(v)})`
                                : `Clic para marcar como pagado (${formatCLPFull(v)})`
                              : ''
                          }
                          data-testid={`proy-cell-${item.orden}-${i}`}
                        >
                          <div className="flex items-center justify-end gap-1">
                            {pagado && <Check className="w-3 h-3 text-emerald-600 flex-shrink-0" />}
                            <span className="pr-1">{formatCLP(v)}</span>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {/* Total row (no incluye pagados) */}
                {data.items.length > 0 && (
                  <tr className="bg-slate-200 dark:bg-slate-800 font-bold border-t-2 border-slate-400 sticky bottom-0">
                    <td className="sticky left-0 z-10 bg-slate-200 dark:bg-slate-800 border px-2 py-1.5" />
                    <td className="sticky left-[64px] z-10 bg-slate-200 dark:bg-slate-800 border px-2 py-1.5 text-center">Σ</td>
                    <td className="sticky left-[104px] z-10 bg-slate-200 dark:bg-slate-800 border px-2 py-1.5">TOTAL PENDIENTE</td>
                    <td className="sticky left-[344px] z-10 bg-slate-200 dark:bg-slate-800 border px-2 py-1.5" />
                    <td className="sticky left-[408px] z-10 bg-slate-200 dark:bg-slate-800 border px-2 py-1.5 text-right tabular-nums">{formatCLP(data.total_general || 0)}</td>
                    <td className="sticky left-[536px] z-10 bg-red-100 dark:bg-red-950/50 border px-2 py-1.5 text-right tabular-nums text-red-700 dark:text-red-300" title={`Vencido total: ${formatCLPFull(data.total_vencido_general || 0)}`} data-testid="proy-total-vencido">{formatCLP(data.total_vencido_general || 0)}</td>
                    {(data.total_por_mes || []).map((v, i) => (
                      <td key={i} className="border px-2 py-1.5 text-right tabular-nums" title={formatCLPFull(v)}>{formatCLP(v)}</td>
                    ))}
                  </tr>
                )}
                {/* Total pagado */}
                {data.items.length > 0 && data.total_pagado_por_mes && data.total_pagado_por_mes.some(v => v > 0) && (
                  <tr className="bg-emerald-100 dark:bg-emerald-950/40 font-semibold sticky bottom-0 text-emerald-800 dark:text-emerald-300">
                    <td className="sticky left-0 z-10 bg-emerald-100 dark:bg-emerald-950/40 border px-2 py-1" />
                    <td className="sticky left-[64px] z-10 bg-emerald-100 dark:bg-emerald-950/40 border px-2 py-1 text-center">✓</td>
                    <td className="sticky left-[104px] z-10 bg-emerald-100 dark:bg-emerald-950/40 border px-2 py-1 text-xs">Ya pagado</td>
                    <td className="sticky left-[344px] z-10 bg-emerald-100 dark:bg-emerald-950/40 border px-2 py-1" />
                    <td className="sticky left-[408px] z-10 bg-emerald-100 dark:bg-emerald-950/40 border px-2 py-1" />
                    <td className="sticky left-[536px] z-10 bg-emerald-100 dark:bg-emerald-950/40 border px-2 py-1" />
                    {data.total_pagado_por_mes.map((v, i) => (
                      <td key={i} className="border px-2 py-1 text-right tabular-nums text-xs line-through opacity-80" title={formatCLPFull(v)}>{formatCLP(v)}</td>
                    ))}
                  </tr>
                )}
                {data.items.length === 0 && (
                  <tr><td colSpan={18} className="p-6 text-center text-muted-foreground">
                    No hay filas en la proyección. Agrega una desde el botón superior.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {/* Dialog create/edit */}
      <Dialog open={editing !== null} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-3xl" data-testid="dialog-proyeccion">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Nueva fila de proyección' : `Editar: ${editing?.nombre || ''}`}</DialogTitle>
            <DialogDescription>Gastos proyectados por mes (12 meses desde el mes actual).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-12 gap-2">
              <div className="col-span-2">
                <Label>Orden</Label>
                <Input type="number" value={form.orden} onChange={(e) => setForm({ ...form, orden: Number(e.target.value) || 0 })} />
              </div>
              <div className="col-span-6">
                <Label>Nombre *</Label>
                <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Ej: Estimado-SUELDOS" data-testid="input-proy-nombre" />
              </div>
              <div className="col-span-2">
                <Label title="Día típico del mes al que cae el pago">Día típico</Label>
                <Input
                  type="number"
                  min={1}
                  max={31}
                  value={form.dia_tipico || 20}
                  onChange={(e) => setForm({ ...form, dia_tipico: Math.max(1, Math.min(31, Number(e.target.value) || 20)) })}
                  data-testid="input-proy-dia-tipico"
                />
              </div>
              <div className="col-span-2">
                <Label>Total</Label>
                <NumberInput allowDecimal={false} allowNegative={false} value={form.total} onChange={(clean) => setForm({ ...form, total: Number(clean) || 0 })} />
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Montos por mes</Label>
              {/* Monto mensual replicable (fill all) */}
              <div className="mb-3 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200">
                <Label className="text-xs text-amber-700 dark:text-amber-300 font-medium">
                  💡 Monto mensual (replicar en los 12 meses)
                </Label>
                <div className="flex gap-2 mt-1">
                  <NumberInput
                    allowDecimal={false}
                    allowNegative={false}
                    placeholder="Ej: 450.000"
                    value={form.monto_repetir || ''}
                    onChange={(clean) => setForm({ ...form, monto_repetir: clean })}
                    data-testid="input-monto-repetir"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const v = Number(form.monto_repetir) || 0;
                      if (v <= 0) { toast({ title: 'Ingrese un monto > 0', variant: 'destructive' }); return; }
                      setForm({
                        ...form,
                        meses: Array(12).fill(v),
                        total: v * 12,
                      });
                      toast({ title: `Aplicado: $${v.toLocaleString('es-CL')} × 12 meses` });
                    }}
                    data-testid="btn-aplicar-repetir"
                  >
                    Aplicar a todos
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setForm({ ...form, meses: Array(12).fill(0), total: 0, monto_repetir: '' })}
                    title="Limpiar todos"
                  >
                    Limpiar
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Tip: aplica primero el monto base, luego ajusta mes por mes abajo si algún mes es distinto.
                </p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {data.meses_header.map((m, i) => (
                  <div key={m.key}>
                    <Label className="text-xs text-muted-foreground">{MESES_NOMBRES[m.month - 1]} {m.year}</Label>
                    <NumberInput
                      allowDecimal={false}
                      allowNegative={false}
                      value={form.meses[i] || 0}
                      onChange={(clean) => updateMes(i, clean)}
                      data-testid={`input-mes-${i}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-save-proy">
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
